<script setup>
import { defineProps, computed, reactive } from 'vue';
import Message from './Message.vue';
import { MESSAGE_TYPES } from './constants.js';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { useMapGetter } from 'dashboard/composables/store.js';
import MessageApi from 'dashboard/api/inbox/message.js';
import DateSeparator from './DateSeparator.vue';

/**
 * Props definition for the component
 * @typedef {Object} Props
 * @property {Array} readMessages - Array of read messages
 * @property {Array} unReadMessages - Array of unread messages
 * @property {Number} currentUserId - ID of the current user
 * @property {Boolean} isAnEmailChannel - Whether this is an email channel
 * @property {Object} inboxSupportsReplyTo - Inbox reply support configuration
 * @property {Array} messages - Array of all messages [These are not in camelcase]
 */
const props = defineProps({
  currentUserId: {
    type: Number,
    required: true,
  },
  firstUnreadId: {
    type: Number,
    default: null,
  },
  isAnEmailChannel: {
    type: Boolean,
    default: false,
  },
  inboxSupportsReplyTo: {
    type: Object,
    default: () => ({ incoming: false, outgoing: false }),
  },
  messages: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['retry']);

// Deep-camelCasing the WHOLE array on every store change re-cloned every
// message (attachments and all) per websocket tick, re-rendering every bubble
// in the open conversation. Identity is a complete change signal here: the
// store never mutates a message in place — ADD_MESSAGE (which updateMessage
// also routes through) replaces the object by index assignment or splice — so
// an unchanged raw object can safely reuse its transformed twin.
const camelCache = new WeakMap();
const allMessages = computed(() => {
  return props.messages.map(raw => {
    let transformed = camelCache.get(raw);
    if (!transformed) {
      transformed = useCamelCase(raw, {
        deep: true,
        stopPaths: ['content_attributes.translations'],
      });
      camelCache.set(raw, transformed);
    }
    return transformed;
  });
});

const currentChat = useMapGetter('getSelectedChat');

// Cache for fetched reply messages to avoid duplicate API calls
const fetchedReplyMessages = reactive(new Map());

/**
 * Fetches a specific message from the API by trying to get messages around it
 * @param {number} messageId - The ID of the message to fetch
 * @param {number} conversationId - The ID of the conversation
 * @returns {Promise<Object|null>} - The fetched message or null if not found/error
 */
const fetchReplyMessage = async (messageId, conversationId) => {
  // Return cached result if already fetched
  if (fetchedReplyMessages.has(messageId)) {
    return fetchedReplyMessages.get(messageId);
  }

  try {
    const response = await MessageApi.getPreviousMessages({
      conversationId,
      before: messageId + 100,
      after: messageId - 100,
    });

    const messages = response.data?.payload || [];
    const targetMessage = messages.find(msg => msg.id === messageId);

    if (targetMessage) {
      const camelCaseMessage = useCamelCase(targetMessage);
      fetchedReplyMessages.set(messageId, camelCaseMessage);
      return camelCaseMessage;
    }

    // Cache null result to avoid repeated API calls
    fetchedReplyMessages.set(messageId, null);
    return null;
  } catch (error) {
    fetchedReplyMessages.set(messageId, null);
    return null;
  }
};

/**
 * Determines if a message should be grouped with the next message
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {Boolean} - Whether the message should be grouped with next
 */
const shouldGroupWithNext = (index, searchList) => {
  if (index === searchList.length - 1) return false;

  const current = searchList[index];
  const next = searchList[index + 1];

  if (next.status === 'failed') return false;

  const nextSenderId = next.senderId ?? next.sender?.id;
  const currentSenderId = current.senderId ?? current.sender?.id;
  const hasSameSender = nextSenderId === currentSenderId;

  const nextMessageType = next.messageType;
  const currentMessageType = current.messageType;

  const areBothTemplates =
    nextMessageType === MESSAGE_TYPES.TEMPLATE &&
    currentMessageType === MESSAGE_TYPES.TEMPLATE;

  if (!hasSameSender || areBothTemplates) return false;

  if (currentMessageType !== nextMessageType) return false;

  // Check if messages are in the same minute by rounding down to nearest minute
  return Math.floor(next.createdAt / 60) === Math.floor(current.createdAt / 60);
};

// Delivery-status strength ordering. A same-minute group hides every message's
// tick except the last one's, so the displayed tick must reflect the WEAKEST
// status in the group — otherwise a group could show "read" while a message
// inside it is only "delivered"/"sent" (an over-claim). failed/unknown statuses
// map to Infinity so they're never picked as a group's weakest (a failed
// message always breaks the group anyway).
const STATUS_RANK = { progress: 0, sent: 1, delivered: 2, read: 3 };

/**
 * Weakest delivery status across the same-minute group that `index` belongs to.
 * Only consumed for the last message of a group (the one that renders the tick);
 * for a standalone message or a uniform group it returns the message's own
 * status, so behaviour is unchanged in the common case.
 * @param {Number} index - Index of the current message
 * @param {Array} searchList - Array of messages to check
 * @returns {String} - The weakest delivery status in the message's group
 */
const groupDisplayStatus = (index, searchList) => {
  // Walk back to the first message of this group.
  let start = index;
  while (start > 0 && shouldGroupWithNext(start - 1, searchList)) {
    start -= 1;
  }
  let weakest = searchList[index].status;
  for (let i = start; i < index; i += 1) {
    const candidate = searchList[i].status;
    if (
      (STATUS_RANK[candidate] ?? Infinity) < (STATUS_RANK[weakest] ?? Infinity)
    ) {
      weakest = candidate;
    }
  }
  return weakest;
};

/**
 * Gets the message that was replied to
 * @param {Object} parentMessage - The message containing the reply reference
 * @returns {Object|null} - The message being replied to, or null if not found
 */
const getInReplyToMessage = parentMessage => {
  if (!parentMessage) return null;

  const inReplyToMessageId =
    parentMessage.contentAttributes?.inReplyTo ??
    parentMessage.content_attributes?.in_reply_to;

  if (!inReplyToMessageId) return null;

  // Try to find in current messages first (raw snake_case)
  let replyMessage = props.messages?.find(msg => msg.id === inReplyToMessageId);

  // Then try store messages (raw snake_case)
  if (!replyMessage && currentChat.value?.messages) {
    replyMessage = currentChat.value.messages.find(
      msg => msg.id === inReplyToMessageId
    );
  }

  // The raw snake_case sources need camelCasing
  if (replyMessage) return useCamelCase(replyMessage);

  // Then check fetch cache (already camelCased when stored)
  if (fetchedReplyMessages.has(inReplyToMessageId)) {
    return fetchedReplyMessages.get(inReplyToMessageId);
  }

  // If still not found and we have conversation context, fetch it
  if (currentChat.value?.id) {
    fetchReplyMessage(inReplyToMessageId, currentChat.value.id);
    return null; // Let UI handle loading state
  }

  return null;
};

/**
 * Determines if a date separator should be shown before this message
 * @param {Number} index - Index of the current message
 * @param {Array} messages - Array of all messages
 * @returns {Boolean} - Whether to show the date separator
 */
const shouldShowDateSeparator = (index, messages) => {
  if (index === 0) return true;
  const currentMsgDate = new Date(
    messages[index].createdAt * 1000
  ).toDateString();
  const prevMsgDate = new Date(
    messages[index - 1].createdAt * 1000
  ).toDateString();
  return currentMsgDate !== prevMsgDate;
};
</script>

<template>
  <ul class="px-4 bg-n-surface-1">
    <slot name="beforeAll" />
    <template v-for="(message, index) in allMessages" :key="message.id">
      <DateSeparator
        v-if="shouldShowDateSeparator(index, allMessages)"
        :date="message.createdAt"
      />
      <slot
        v-if="firstUnreadId && message.id === firstUnreadId"
        name="unreadBadge"
      />
      <Message
        v-bind="message"
        :is-email-inbox="isAnEmailChannel"
        :in-reply-to="getInReplyToMessage(message)"
        :group-with-next="shouldGroupWithNext(index, allMessages)"
        :group-display-status="groupDisplayStatus(index, allMessages)"
        :inbox-supports-reply-to="inboxSupportsReplyTo"
        :current-user-id="currentUserId"
        data-clarity-mask="True"
        @retry="emit('retry', message)"
      />
    </template>
    <slot name="after" />
  </ul>
</template>
